import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Chip, Typography, Divider, IconButton, Tooltip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { UnifiedCard, buildCardListSx, buildListItemTextSx, buildCardCaptionSx } from './UIComponents';
import {
  SlideshowIcon,
  EditIcon,
  DescriptionIcon,
  PublicIcon,
  SearchIcon,
  LinkIcon,
  CheckCircleIcon,
  SecurityIcon,
  CheckIcon,
  CloseIcon
} from '../../../../shared/icons';
import EnhancedMarkdownRenderer from './EnhancedMarkdownRenderer';
import { getChatRadii, getChatBorders } from '../chatStyles';
import {
  getRestackTokens,
  parseResearchReport,
  extractReportTitle,
  formatDurationLabel,
  normalizeTodoStatus,
  normalizeSourceUrl,
  getSourceDomain
} from '../utils';

/**
 * Unified slide builder card - handles both slide creation and editing
 * Replaces SlideCreationCard and SlideEditCard with a single component
 * that adapts based on the operation prop
 */
const SlideBuilderCardComponent = ({
  operation = 'create',  // 'create' or 'edit'
  status,
  count,
  isLoading = false,
  slideIds = [],
  slideIndices = [],
  completedCount = 0,
  progressSlides = [],
  currentSlideCount = 0,
  // Edit-specific props (backward compatibility)
  editedSlides = [],
  slideNumbers = []
}) => {
  const theme = useTheme();
  const { tokens } = theme.restack;
  const cardListSx = React.useMemo(() => buildCardListSx(theme, tokens), [theme, tokens]);
  const listItemSx = React.useMemo(() => buildListItemTextSx(theme, tokens), [theme, tokens]);
  const captionSx = React.useMemo(() => buildCardCaptionSx(theme), [theme]);

  const isCreation = operation === 'create';
  const isEdit = operation === 'edit';

  // Dynamic status labels based on operation
  const getStatusLabel = (statusKey) => {
    if (isCreation) {
      switch (statusKey) {
        case 'added':
          return 'Added';
        case 'in-progress':
          return 'Adding';
        default:
          return 'Queued';
      }
    } else {
      switch (statusKey) {
        case 'added':
        case 'changed':
          return 'Updated';
        case 'in-progress':
          return 'Updating';
        default:
          return 'Queued';
      }
    }
  };

  const deriveStatus = (completed, inProgress, slideIndex = null, allSlides = [], slideIsPlaceholder = false) => {
    // PRIORITY 1: Explicitly completed - ALWAYS show checkmark
    // This must check completed FIRST before anything else
    if (completed === true || completed === 'true') {
      return isCreation ? 'added' : 'changed';
    }

    // PRIORITY 2: If not loading (session ended), non-placeholder slides are completed
    // This handles the case where the backend didn't send a completed event
    if (!isLoading && !slideIsPlaceholder) {
      return isCreation ? 'added' : 'changed';
    }

    // PRIORITY 3: Explicitly in progress (only when still loading)
    // Don't show in-progress state when session has ended (isLoading is false)
    if (isLoading && (inProgress === true || inProgress === 'true')) {
      return 'in-progress';
    }

    // PRIORITY 4: When loading, determine if this should be in-progress
    // If no slide is explicitly marked as in-progress, the first incomplete slide
    // should be marked as in-progress (fallback for timing issues)
    if (isLoading && slideIndex !== null && allSlides.length > 0) {
      const hasAnyInProgress = allSlides.some(s => s.inProgress === true);
      if (!hasAnyInProgress) {
        // Find first incomplete slide index
        const firstIncompleteIdx = allSlides.findIndex(s => !s.completed && !s.isPlaceholder);
        if (slideIndex === firstIncompleteIdx) {
          return 'in-progress';
        }
      }
    }

    // PRIORITY 5: Pending/queued state
    // If loading but not explicitly completed or in progress,
    // this is a pending slide (not started yet)
    if (isLoading) {
      return 'pending';
    }

    // FALLBACK: Default to completed state (should rarely reach here)
    return isCreation ? 'added' : 'changed';
  };

  const slideItems = React.useMemo(() => {
    const mapped = [];

    const resolveDisplayNumber = (primary, fallback) => {
      const normalizedPrimary = typeof primary === 'string' ? Number(primary) : primary;
      if (Number.isFinite(normalizedPrimary)) {
        return normalizedPrimary;
      }
      const normalizedFallback = typeof fallback === 'string' ? Number(fallback) : fallback;
      if (Number.isFinite(normalizedFallback)) {
        return normalizedFallback;
      }
      return null;
    };

    const buildSlideTitle = (displayNumber, explicitTitle, prefix = 'Slide') => {
      if (explicitTitle) {
        return explicitTitle;
      }
      if (Number.isFinite(displayNumber)) {
        return `${prefix} ${displayNumber}`;
      }
      return prefix;
    };

    // Handle progressSlides (creation mode)
    if (progressSlides && progressSlides.length > 0) {
      progressSlides.forEach((slide, index) => {
        const inferredOrdinal = slide?.slideIndex !== undefined ? slide.slideIndex + 1 : index + 1;
        const displayNumber = resolveDisplayNumber(slide?.displayNumber, slide?.slideNumber ?? inferredOrdinal);
        let title;
        let isPending = false;
        let undoId = null;
        let contentPreview = [];

        // Detect placeholders vs real slides
        if (slide.isPlaceholder) {
          title = buildSlideTitle(displayNumber, slide?.title, 'Pending Slide');
          isPending = true;
          undoId = null;
          contentPreview = [];
        } else {
          title = buildSlideTitle(displayNumber, slide?.title);
          undoId = slide.id || slide.slideId || null;

          // Convert contentPreview to changes format
          if (slide.contentPreview) {
            if (slide.contentPreview.headline) {
              contentPreview.push({
                type: 'heading',
                textAfter: slide.contentPreview.headline,
                textBefore: '',
                selector: 'h1'
              });
            }
            if (slide.contentPreview.subtitle) {
              contentPreview.push({
                type: 'paragraph',
                textAfter: slide.contentPreview.subtitle,
                textBefore: '',
                selector: 'p'
              });
            }
          }
        }

        mapped.push({
          id: slide.id || `progress-${index}`,
          undoId,
          slideNumber: displayNumber ?? inferredOrdinal,
          displayNumber,
          title,
          // FIX: Pass index, progressSlides, and isPlaceholder for proper status derivation
          status: deriveStatus(slide.completed, slide.inProgress, index, progressSlides, isPending),
          // Only show "Preparing..." when both isPending AND isLoading are true
          meta: slide.stage || (isPending && isLoading ? 'Preparing...' : null),
          contentPreview,
          changes: slide.changes || [],
          isPlaceholder: isPending
        });
      });
    }
    // Handle editedSlides (edit mode - backward compatibility)
    else if (editedSlides && editedSlides.length > 0) {
      editedSlides.forEach((slide) => {
        const inferredOrdinal = slide?.slideIndex !== undefined ? slide.slideIndex + 1 : slide?.slideNumber;
        const displayNumber = resolveDisplayNumber(slide?.displayNumber, inferredOrdinal);
        mapped.push({
          id: slide.slideId,
          undoId: slide.slideId,
          slideNumber: inferredOrdinal ?? displayNumber ?? null,
          displayNumber,
          title: buildSlideTitle(displayNumber ?? inferredOrdinal, slide.title),
          status: 'changed',
          meta: null,
          changes: slide.changes || [],
          contentPreview: []
        });
      });
    }
    // Handle slideNumbers (edit mode - backward compatibility)
    else if (slideNumbers && slideNumbers.length > 0) {
      slideNumbers.forEach((slideNum, index) => {
        const displayNumber = resolveDisplayNumber(slideNum, slideNum);
        mapped.push({
          id: `slide-${slideNum}`,
          undoId: editedSlides?.[index]?.slideId || null,
          slideNumber: slideNum,
          displayNumber,
          title: buildSlideTitle(displayNumber, null),
          status: deriveStatus(index < completedCount, isLoading && index === completedCount),
          meta: isLoading && index === completedCount ? 'Applying edits…' : null,
          changes: [],
          contentPreview: []
        });
      });
    }
    // Handle slideIndices
    else if (slideIndices && slideIndices.length > 0) {
      slideIndices.forEach((slideIndex, idx) => {
        const slideNumber = slideIndex + 1;
        const displayNumber = resolveDisplayNumber(null, slideNumber);
        mapped.push({
          id: `index-${slideIndex}`,
          undoId: slideIds?.[idx] || null,
          slideNumber,
          displayNumber,
          title: `Slide ${slideNumber}`,
          status: deriveStatus(idx < completedCount, isLoading && idx === completedCount),
          meta: isLoading && idx === completedCount ? (isCreation ? 'Creating content…' : 'Applying edits…') : null,
          changes: [],
          contentPreview: []
        });
      });
    }
    // Handle slideIds
    else if (slideIds && slideIds.length > 0) {
      slideIds.forEach((slideId, idx) => {
        const slideNumber = (slideIndices[idx] !== undefined ? slideIndices[idx] + 1 : currentSlideCount + idx + 1);
        const displayNumber = resolveDisplayNumber(null, slideNumber);
        mapped.push({
          id: slideId,
          undoId: slideId,
          slideNumber,
          displayNumber,
          title: `Slide ${slideNumber}`,
          status: deriveStatus(true, false),
          meta: null,
          changes: [],
          contentPreview: []
        });
      });
    }
    // Fallback: generate generic slides
    else {
      const baseTotal = count || 1;
      for (let i = 0; i < baseTotal; i += 1) {
        const slideNumber = currentSlideCount + i + 1;
        const displayNumber = resolveDisplayNumber(null, slideNumber);
        mapped.push({
          id: `generated-${i}`,
          undoId: slideIds?.[i] || null,
          slideNumber,
          displayNumber,
          title: `Slide ${slideNumber}`,
          status: deriveStatus(i < completedCount, isLoading && i === completedCount),
          meta: isLoading && i === completedCount ? (isCreation ? 'Creating content…' : 'Applying edits…') : null,
          changes: [],
          contentPreview: []
        });
      }
    }

    return mapped;
  }, [progressSlides, editedSlides, slideNumbers, slideIndices, slideIds, count, completedCount, isLoading, currentSlideCount, isCreation]);

  const normalizedStatus = typeof status === 'string' ? status.replace(/[✅⚠️⭐️🔥✨]+\s*/gu, '').trim() : '';
  const headline = isCreation ? 'Creating Slides' : 'Editing Slides';
  const subtitle = normalizedStatus || undefined;

  // Simple icon: always use document icon for slides
  const icon = <DescriptionIcon />;

  const getStatusChipSx = (paletteTheme, statusKey) => {
    const baseColor = {
      added: paletteTheme.palette.success.main,
      changed: paletteTheme.palette.success.main,
      'in-progress': paletteTheme.palette.info.main,
      pending: paletteTheme.palette.warning.main
    }[statusKey] || paletteTheme.palette.text.secondary;

    return {
      backgroundColor: alpha(baseColor, paletteTheme.palette.mode === 'dark' ? 0.2 : 0.12),
      color: baseColor,
      fontWeight: paletteTheme.typography.fontWeightMedium,
      letterSpacing: 0.1,
    };
  };

  return (
    <UnifiedCard
      icon={icon}
      title={headline}
      subtitle={subtitle}
      isLoading={isLoading}
      isCollapsible={false}
      defaultExpanded={false}
      centerTitle={true}
    >
      <Box component="ul" sx={cardListSx}>
        {slideItems.map((item, index) => {
          return (
            <Box
              key={`${item.id || item.slideNumber || 'slide'}-${index}`}
              component="li"
              sx={(paletteTheme) => ({
                ...listItemSx,
                display: 'flex',
                flexDirection: 'column',
                gap: paletteTheme.spacing(tokens.spacing.quark),
                paddingBottom: paletteTheme.spacing(tokens.spacing.quark),
                // Consistent padding for all slides (alignment)
                padding: paletteTheme.spacing(1, 1.25),
                marginLeft: paletteTheme.spacing(-0.5),
                marginRight: paletteTheme.spacing(-0.5),
                marginTop: paletteTheme.spacing(-0.25),
                marginBottom: paletteTheme.spacing(-0.25),
                '&:last-of-type': {
                  paddingBottom: item.status === 'in-progress' ? paletteTheme.spacing(1) : 0
                },
                // Subtle background for in-progress slide (no shimmer)
                ...(item.status === 'in-progress' && {
                  backgroundColor: alpha(paletteTheme.palette.primary.main, paletteTheme.palette.mode === 'dark' ? 0.04 : 0.02),
                  borderRadius: getChatRadii(paletteTheme).nested
                })
              })}
            >
              <Box
                sx={(paletteTheme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: paletteTheme.spacing(tokens.spacing.sm)
                })}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={(paletteTheme) => ({
                      color: paletteTheme.palette.text.primary,
                      fontWeight: paletteTheme.typography.fontWeightMedium,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    })}
                  >
                    {(() => {
                      const displayNumber = Number.isFinite(item.displayNumber)
                        ? item.displayNumber
                        : (Number.isFinite(item.slideNumber) ? item.slideNumber : index + 1);
                      return `Slide ${displayNumber}`;
                    })()}
                  </Typography>
                  {item.meta && (
                    <Typography
                      variant="caption"
                      sx={(paletteTheme) => ({
                        ...captionSx,
                        color: paletteTheme.palette.text.secondary,
                        display: 'block',
                        marginTop: paletteTheme.spacing(tokens.spacing.quark),
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      })}
                    >
                      {item.meta.replace(/[✅⚠️⭐️🔥✨]+\s*/gu, '').trim()}
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={(paletteTheme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: paletteTheme.spacing(tokens.spacing.quark)
                  })}
                >
                  {/* Show checkmark icon only for completed slides */}
                  {(item.status === 'added' || item.status === 'changed') && (
                    <CheckCircleIcon
                      sx={(paletteTheme) => ({
                        fontSize: paletteTheme.typography.pxToRem(18),
                        color: paletteTheme.palette.success.main
                      })}
                    />
                  )}
                  {/* In-progress: No icon (subtle background shows status) */}
                  {/* Queued/Pending: No icon */}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </UnifiedCard>
  );
};

export const SlideBuilderCard = React.memo(SlideBuilderCardComponent);

// Sources shown before the list collapses. Twenty fetched pages must not push
// the rest of the transcript off a task pane, but a short list should never
// hide behind a click.
const VISIBLE_SOURCE_LIMIT = 6;

// The research export writes an HTML file. Source titles and URLs are
// web-derived, so they are escaped before they reach that document.
const escapeExportHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const ResearchCardComponent = ({
  query,
  keyFindings = [],  // Structured key findings array
  citationsDuration = null,
  isLoading = false,
  status = null,
  report = null,  // DEPRECATED: Use fullReport instead (backwards compat)
  fullReport = null,  // Primary report text - parsed for citations
  searchQueries = [],  // WebSearch queries used during research
  fetchedPages = [],   // WebFetch URLs the agent actually read
  // DEPRECATED: No longer used - citations are parsed from fullReport
  citations,
  sourceSummaries = []
}) => {
  const theme = useTheme();
  const tokens = getRestackTokens(theme);
  const [hasStartedStreaming, setHasStartedStreaming] = useState(false);
  const [showAllSources, setShowAllSources] = useState(false);

  // Track when streaming starts.
  // Also flip on saved data — citations, search queries OR fetched pages — so
  // the card renders correctly after a page reload (when isLoading is already
  // false).
  useEffect(() => {
    if (
      isLoading
      || (citations && citations.length > 0)
      || (Array.isArray(searchQueries) && searchQueries.length > 0)
      || (Array.isArray(fetchedPages) && fetchedPages.length > 0)
    ) {
      setHasStartedStreaming(true);
    }
  }, [isLoading, citations, searchQueries, fetchedPages]);

  // Parse fullReport to extract citations and clean content
  const parsedReport = useMemo(() => {
    const reportText = fullReport || report;
    if (!reportText || typeof reportText !== 'string') {
      return {
        content: '',
        citations: [],
        citationMap: new Map()
      };
    }

    const parsed = parseResearchReport(reportText);
    const citationMap = new Map(parsed.citations.map(c => [c.number, { title: c.title, url: c.url }]));

    return {
      content: parsed.content,
      citations: parsed.citations,
      citationMap
    };
  }, [fullReport, report]);

  // Extract title from report markdown (MUST be before early return)
  const reportTitle = useMemo(() => {
    const reportText = fullReport || report;
    return extractReportTitle(reportText);
  }, [fullReport, report]);

  console.log('📊 RESEARCH CARD PROPS:', {
    query,
    keyFindingsCount: keyFindings?.length || 0,
    citationsCount: citations?.length || 0,
    parsedCitationsCount: parsedReport.citations.length,
    isLoading,
    status,
    hasReport: !!report,
    hasFullReport: !!fullReport,
    reportLength: report?.length || 0,
    fullReportLength: fullReport?.length || 0,
    reportPreview: (fullReport || report)?.substring(0, 100),
    hasSearchQueries: Array.isArray(searchQueries),
    searchQueriesCount: searchQueries?.length || 0,
    searchQueriesPreview: searchQueries?.slice(0, 2)
  });

  // Titles for fetched pages. A WebFetch tool result never reaches the client,
  // so the only honest title for a page is the one a search result already
  // gave for that exact URL. Anything without a match shows its URL instead —
  // the card never invents a name for a source.
  const titlesByUrl = useMemo(() => {
    const map = new Map();
    const record = (entry) => {
      const key = normalizeSourceUrl(entry?.url);
      const title = typeof entry?.title === 'string' ? entry.title.trim() : '';
      if (!key || !title || map.has(key)) return;
      // A citation whose "title" is just the URL adds nothing.
      if (normalizeSourceUrl(title) === key) return;
      map.set(key, title);
    };

    (Array.isArray(citations) ? citations : []).forEach(record);
    parsedReport.citations.forEach(record);
    return map;
  }, [citations, parsedReport.citations]);

  // The provenance list: every distinct search the agent ran and every
  // distinct page it read, in the order they happened. This is the whole
  // point of the card for a finance user — which sources the answer stands on.
  const sourceEntries = useMemo(() => {
    const entries = [];
    const seenQueries = new Set();
    const seenPages = new Set();

    (Array.isArray(searchQueries) ? searchQueries : []).forEach((item, index) => {
      const text = typeof item?.query === 'string' ? item.query.trim() : '';
      if (!text) return;
      const key = text.toLowerCase();
      const existing = seenQueries.has(key)
        ? entries.find(entry => entry.kind === 'search' && entry.key === key)
        : null;
      if (existing) {
        // A repeated search is listed once, but it still shimmers while any
        // of its runs is in flight.
        existing.completed = existing.completed && item?.completed === true;
        return;
      }
      seenQueries.add(key);
      entries.push({
        kind: 'search',
        key,
        id: `search-${item?.toolId || key}-${index}`,
        primary: text,
        completed: item?.completed === true,
        timestamp: item?.timestamp || null
      });
    });

    (Array.isArray(fetchedPages) ? fetchedPages : []).forEach((item, index) => {
      const key = normalizeSourceUrl(item?.url);
      if (!key || seenPages.has(key)) return;
      seenPages.add(key);
      const url = item.url.trim();
      const domain = getSourceDomain(url);
      const title = titlesByUrl.get(key) || null;
      let detail = title;
      if (!detail) {
        // No title anywhere, so show the rest of the URL. The domain already
        // leads the row, so repeating it here would just eat the width.
        try {
          const parsed = new URL(url);
          detail = `${parsed.pathname}${parsed.search}`.replace(/^\/$/, '');
        } catch {
          detail = url;
        }
      }
      entries.push({
        kind: 'page',
        key,
        id: `page-${item?.toolId || key}-${index}`,
        primary: domain || url,
        detail: detail || null,
        url,
        completed: true,
        timestamp: item?.timestamp || null
      });
    });

    // Sort chronologically only when every entry can be placed. Mixed or
    // missing timestamps fall back to insertion order rather than guessing.
    const stamps = entries.map(entry => Date.parse(entry.timestamp));
    if (entries.length > 1 && stamps.every(Number.isFinite)) {
      return entries
        .map((entry, index) => ({ entry, index, ms: stamps[index] }))
        .sort((a, b) => (a.ms - b.ms) || (a.index - b.index))
        .map(({ entry }) => entry);
    }

    return entries;
  }, [searchQueries, fetchedPages, titlesByUrl]);

  const searchCount = sourceEntries.filter(entry => entry.kind === 'search').length;
  const pageCount = sourceEntries.length - searchCount;
  const visibleSourceEntries = showAllSources
    ? sourceEntries
    : sourceEntries.slice(0, VISIBLE_SOURCE_LIMIT);
  const hiddenSourceCount = sourceEntries.length - visibleSourceEntries.length;

  const sourcesSummary = [
    searchCount > 0 ? `${searchCount} ${searchCount === 1 ? 'search' : 'searches'}` : null,
    pageCount > 0 ? `${pageCount} ${pageCount === 1 ? 'page' : 'pages'}` : null
  ].filter(Boolean).join(' · ');

  // Always show the card if loading OR if we have content OR if we've started streaming
  // OR if it carries persisted research data (citations / searches / fetched pages)
  // so the card survives a page reload even when nothing is streaming and there's no
  // report text. This ensures immediate feedback when research is triggered AND keeps
  // the card visible after a reload of a chat that used inline WebSearch or WebFetch
  // (no full report).
  const shouldRender = (
    isLoading
    || hasStartedStreaming
    || !!parsedReport.content
    || (Array.isArray(keyFindings) && keyFindings.length > 0)
    || (Array.isArray(citations) && citations.length > 0)
    || sourceEntries.length > 0
  );

  // Memoize key findings rendering to prevent flicker
  const keyFindingsSection = useMemo(() => {
    if (isLoading || !Array.isArray(keyFindings) || keyFindings.length === 0) {
      return null;
    }
    return (
      <Box sx={{ padding: theme.spacing(tokens.spacing.md) }}>
        <Typography sx={{
          fontSize: theme.typography.pxToRem(12),
          fontWeight: theme.typography.fontWeightMedium,
          color: alpha(theme.palette.text.primary, 0.6),
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: theme.spacing(tokens.spacing.sm)
        }}>
          Key Findings
        </Typography>
        <Box component="ul" sx={{
          margin: 0,
          paddingLeft: theme.spacing(2.5),
          listStyle: 'disc',
          listStylePosition: 'outside',
          '& li': {
            fontSize: theme.typography.pxToRem(13),
            lineHeight: 1.6,
            color: theme.palette.text.primary,
            fontWeight: theme.typography.fontWeightRegular,
            marginBottom: theme.spacing(tokens.spacing.sm),
            '& > *': {
              display: 'inline'
            }
          },
          '& li:last-child': {
            marginBottom: 0
          }
        }}>
          {keyFindings.map((finding, idx) => (
            <Box component="li" key={idx}>
              <EnhancedMarkdownRenderer
                content={finding}
                theme={theme}
                inline
                citationMap={parsedReport.citationMap}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }, [isLoading, keyFindings, theme, tokens, parsedReport.citationMap]);

  // Memoize research content rendering to prevent flicker
  const researchContentSection = useMemo(() => {
    if (isLoading || !parsedReport.content) {
      return null;
    }
    return (
      <Box sx={{ padding: theme.spacing(tokens.spacing.md) }}>
        <EnhancedMarkdownRenderer
          content={parsedReport.content}
          theme={theme}
          citationMap={parsedReport.citationMap}
        />
      </Box>
    );
  }, [isLoading, parsedReport.content, parsedReport.citationMap, theme, tokens]);

  // Memoize citations list rendering to prevent flicker
  const citationsSection = useMemo(() => {
    if (isLoading || parsedReport.citations.length === 0) {
      return null;
    }
    return (
      <Box sx={{ padding: theme.spacing(tokens.spacing.md) }}>
        <Typography sx={{
          fontSize: theme.typography.pxToRem(12),
          fontWeight: theme.typography.fontWeightMedium,
          color: alpha(theme.palette.text.primary, 0.6),
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: theme.spacing(tokens.spacing.sm)
        }}>
          References
        </Typography>
        <Box>
          {parsedReport.citations.map((citation) => {
            const domain = extractDomain(citation.title || '', citation.url);

            return (
              <Box
                key={`citation-${citation.number}`}
                component="a"
                href={citation.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: theme.spacing(tokens.spacing.sm),
                  padding: theme.spacing(tokens.spacing.sm),
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'background-color 0.15s',
                  cursor: 'pointer',
                  borderRadius: getChatRadii(theme).nested,
                  marginBottom: theme.spacing(tokens.spacing.sm),
                  border: getChatBorders(theme).resting,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  },
                  '&:last-child': {
                    marginBottom: 0
                  }
                }}
              >
                {/* Citation number badge */}
                <Box sx={{
                  width: theme.spacing(2.75),
                  height: theme.spacing(2.75),
                  borderRadius: getChatRadii(theme).circle,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: theme.spacing(0.25)
                }}>
                  <Typography sx={{
                    fontSize: theme.typography.pxToRem(11),
                    fontWeight: theme.typography.fontWeightBold,
                    color: theme.palette.text.primary,
                    lineHeight: 1
                  }}>
                    {citation.number}
                  </Typography>
                </Box>

                {/* Citation content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Citation title */}
                  <Typography sx={{
                    fontSize: theme.typography.pxToRem(13),
                    fontWeight: theme.typography.fontWeightMedium,
                    color: theme.palette.text.primary,
                    lineHeight: 1.4,
                    marginBottom: theme.spacing(0.25),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {citation.title}
                  </Typography>

                  {/* Domain */}
                  <Typography sx={{
                    fontSize: theme.typography.pxToRem(11),
                    fontWeight: theme.typography.fontWeightRegular,
                    color: theme.palette.text.secondary,
                    fontFamily: 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {domain}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }, [isLoading, parsedReport.citations, theme, tokens]);

  if (!shouldRender) {
    console.log('⚠️ RESEARCH CARD RETURNING NULL - no content and never started');
    return null;
  }

  // Calculate total duration if available
  const totalDuration = citationsDuration;

  const normalizedStatus = (status || '').trim();
  const statusLower = normalizedStatus.toLowerCase();
  const isErrorStatus = normalizedStatus.length > 0 && (
    statusLower.includes('error') ||
    statusLower.includes('fail') ||
    statusLower.includes('unable') ||
    statusLower.includes('unavailable')
  );

  const citationsCount = parsedReport.citations.length;
  const sourcesLabel = citationsCount > 0
    ? `${citationsCount} source${citationsCount !== 1 ? 's' : ''}`
    : null;

  // Simple title: always "Research"
  const finalTitle = 'Research';
  const finalSubtitle = totalDuration || undefined;

  // Export to PDF function
  const handleExportPDF = () => {
    try {
      // Create HTML document from markdown
      const reportText = parsedReport.content || fullReport || report || '';
      const title = reportTitle || 'Research Report';
      const generatedDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      const generatedTime = new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });

      // Build citation URL mapping for inline links
      const citationUrlMap = {};
      if (parsedReport.citations && parsedReport.citations.length > 0) {
        parsedReport.citations.forEach(citation => {
          citationUrlMap[citation.number] = citation.url;
        });
      }

      // Build methodology section from the same provenance list the card
      // shows: the searches that were run and the pages that were read.
      // Values come from tool arguments and tool results, so every one of
      // them is escaped before it goes into the exported document.
      let methodologyHTML = '';
      if (sourceEntries.length > 0) {
        methodologyHTML = `
          <section class="methodology">
            <h2>Research Methodology</h2>
            <p class="methodology-intro">This report was compiled from the following sources:</p>
            <div class="queries-list">
              ${sourceEntries.map((entry, idx) => {
                const text = entry.kind === 'search'
                  ? `Search: ${entry.primary}`
                  : `Read: ${entry.url}${entry.detail && entry.detail !== entry.url ? ` (${entry.detail})` : ''}`;
                return `
                <div class="query-item">
                  <span class="query-number">${idx + 1}</span>
                  <span class="query-text">${escapeExportHtml(text)}</span>
                </div>
              `;
              }).join('')}
            </div>
          </section>
        `;
      }

      // Build key findings section
      let keyFindingsHTML = '';
      if (keyFindings && keyFindings.length > 0) {
        keyFindingsHTML = `
          <section class="key-findings">
            <h2>Key Findings</h2>
            <ul class="findings-list">
              ${keyFindings.map(finding => `<li>${finding}</li>`).join('')}
            </ul>
          </section>
        `;
      }

      // Build references section
      let referencesHTML = '';
      if (parsedReport.citations && parsedReport.citations.length > 0) {
        referencesHTML = `
          <section class="references">
            <h2>References</h2>
            <div class="references-list">
              ${parsedReport.citations.map(citation => {
                let domain = 'web';
                try {
                  const urlObj = new URL(citation.url);
                  domain = urlObj.hostname.replace('www.', '');
                } catch (e) { /* ignore */ }
                return `
                  <div class="reference-item">
                    <div class="reference-number">${citation.number}</div>
                    <div class="reference-content">
                      <div class="reference-title">${citation.title}</div>
                      <a href="${citation.url}" target="_blank" rel="noopener noreferrer" class="reference-url">${domain}</a>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </section>
        `;
      }

      // Convert markdown to HTML with improved formatting
      let contentHTML = reportText
        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
        .replace(/^# (.+)$/gm, '<h1 class="content-heading">$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/^- (.+)$/gm, '<li>$1</li>')
        .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
        .replace(/\n\n/g, '</p><p>');

      // Replace citation numbers with clickable superscript links
      contentHTML = contentHTML.replace(/\[(\d+)\]/g, (match, num) => {
        const url = citationUrlMap[num];
        if (url) {
          return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="citation-link"><sup>[${num}]</sup></a>`;
        }
        return `<sup class="citation-number">[${num}]</sup>`;
      });

      contentHTML = `<div class="report-content"><p>${contentHTML}</p></div>`;

      const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="generator" content="Restack AI">
  <title>${title} - Research Report</title>
  <style>
    :root {
      --primary: #b45309;
      --primary-light: #fef3c7;
      --primary-dark: #92400e;
      --accent: #d97706;
      --text-primary: #292524;
      --text-secondary: #57534e;
      --text-muted: #78716c;
      --border: #e7e5e4;
      --background: #ffffff;
      --background-subtle: #fafaf9;
      --background-warm: #fffbeb;
    }

    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.7;
      color: var(--text-primary);
      background: var(--background);
      font-size: 15px;
      -webkit-font-smoothing: antialiased;
    }

    .container {
      max-width: 800px;
      margin: 0 auto;
      padding: 48px 32px;
    }

    /* Header */
    .report-header {
      margin-bottom: 48px;
      padding-bottom: 32px;
      border-bottom: 1px solid var(--border);
    }

    .report-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      background: var(--background-warm);
      color: var(--primary);
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border-radius: 4px;
      margin-bottom: 16px;
      border: 1px solid var(--primary-light);
    }

    .report-title {
      font-size: 32px;
      font-weight: 700;
      color: var(--text-primary);
      line-height: 1.3;
      margin-bottom: 16px;
    }

    .report-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      color: var(--text-muted);
      font-size: 13px;
    }

    .meta-item {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .report-query {
      margin-top: 20px;
      padding: 16px 20px;
      background: var(--background-warm);
      border-left: 3px solid var(--accent);
      border-radius: 0 8px 8px 0;
    }

    .query-label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--text-muted);
      margin-bottom: 6px;
    }

    .query-text {
      font-size: 15px;
      color: var(--text-primary);
      font-style: italic;
    }

    /* Sections */
    section {
      margin-bottom: 40px;
    }

    h2 {
      font-size: 20px;
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 2px solid var(--primary-light);
    }

    h3 {
      font-size: 17px;
      font-weight: 600;
      color: var(--text-primary);
      margin-top: 24px;
      margin-bottom: 12px;
    }

    p {
      margin-bottom: 16px;
      color: var(--text-secondary);
    }

    /* Methodology */
    .methodology-intro {
      margin-bottom: 16px;
      color: var(--text-secondary);
    }

    .queries-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .query-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px 16px;
      background: var(--background-subtle);
      border-radius: 8px;
      border: 1px solid var(--border);
    }

    .query-number {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--primary);
      color: white;
      font-size: 12px;
      font-weight: 600;
      border-radius: 50%;
    }

    .query-item .query-text {
      font-family: 'SF Mono', Monaco, 'Courier New', monospace;
      font-size: 13px;
      color: var(--text-secondary);
      font-style: normal;
    }

    /* Key Findings */
    .findings-list {
      list-style: none;
      padding: 0;
    }

    .findings-list li {
      position: relative;
      padding: 12px 16px 12px 40px;
      margin-bottom: 8px;
      background: var(--background-subtle);
      border-radius: 8px;
      color: var(--text-secondary);
    }

    .findings-list li::before {
      content: '✓';
      position: absolute;
      left: 14px;
      color: var(--primary);
      font-weight: 700;
    }

    /* Report Content */
    .report-content {
      color: var(--text-secondary);
    }

    .report-content h1.content-heading {
      font-size: 24px;
      margin-top: 32px;
      margin-bottom: 16px;
    }

    .report-content ul {
      padding-left: 24px;
      margin-bottom: 16px;
    }

    .report-content li {
      margin-bottom: 8px;
    }

    .citation-link {
      color: var(--primary);
      text-decoration: none;
      font-weight: 500;
    }

    .citation-link:hover {
      text-decoration: underline;
    }

    .citation-number {
      color: var(--primary);
      font-weight: 500;
    }

    /* References */
    .references-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .reference-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 16px;
      background: var(--background-subtle);
      border: 1px solid var(--border);
      border-radius: 8px;
      transition: border-color 0.2s;
    }

    .reference-item:hover {
      border-color: var(--primary);
    }

    .reference-number {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--primary-light);
      color: var(--primary);
      font-size: 13px;
      font-weight: 700;
      border-radius: 50%;
    }

    .reference-content {
      flex: 1;
      min-width: 0;
    }

    .reference-title {
      font-weight: 500;
      color: var(--text-primary);
      margin-bottom: 4px;
      line-height: 1.4;
    }

    .reference-url {
      font-size: 12px;
      color: var(--primary);
      text-decoration: none;
      font-family: 'SF Mono', Monaco, 'Courier New', monospace;
    }

    .reference-url:hover {
      text-decoration: underline;
    }

    /* Footer */
    .report-footer {
      margin-top: 48px;
      padding-top: 24px;
      border-top: 1px solid var(--border);
      text-align: center;
    }

    .footer-brand {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: var(--background-warm);
      border: 1px solid var(--primary-light);
      border-radius: 8px;
      margin-bottom: 12px;
    }

    .footer-logo {
      width: 20px;
      height: 20px;
    }

    .footer-text {
      font-size: 13px;
      font-weight: 500;
      color: var(--text-secondary);
    }

    .footer-timestamp {
      font-size: 12px;
      color: var(--text-muted);
    }

    /* Print Styles */
    @media print {
      body {
        font-size: 12px;
      }

      .container {
        padding: 24px;
        max-width: none;
      }

      .report-header {
        margin-bottom: 32px;
      }

      section {
        break-inside: avoid;
        margin-bottom: 24px;
      }

      .reference-item {
        break-inside: avoid;
      }

      .report-footer {
        margin-top: 32px;
      }

      a {
        text-decoration: none;
      }
    }

    @media (max-width: 600px) {
      .container {
        padding: 24px 16px;
      }

      .report-title {
        font-size: 24px;
      }

      .report-meta {
        flex-direction: column;
        gap: 8px;
      }
    }
  </style>
</head>
<body>
  <div class="container">
    <header class="report-header">
      <div class="report-badge">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z"/>
        </svg>
        Research Report
      </div>
      <h1 class="report-title">${title}</h1>
      <div class="report-meta">
        <div class="meta-item">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" opacity="0.6">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/>
          </svg>
          Generated on ${generatedDate} at ${generatedTime}
        </div>
      </div>
      ${query ? `
        <div class="report-query">
          <div class="query-label">Original Research Query</div>
          <div class="query-text">"${query}"</div>
        </div>
      ` : ''}
    </header>

    ${keyFindingsHTML}

    ${methodologyHTML}

    <section class="main-content">
      <h2>Research Findings</h2>
      ${contentHTML}
    </section>

    ${referencesHTML}

    <footer class="report-footer">
      <div class="footer-brand">
        <svg class="footer-logo" viewBox="0 0 24 24" fill="var(--primary)">
          <path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z"/>
        </svg>
        <span class="footer-text">Generated by Restack AI</span>
      </div>
      <div class="footer-timestamp">
        ${generatedDate} at ${generatedTime}
      </div>
    </footer>
  </div>
</body>
</html>`;

      // Create blob and download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const sanitizedTitle = title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `research-report-${sanitizedTitle}-${dateStr}.html`;
      document.body.appendChild(link);
      link.click();

      // Safely remove link with error handling to prevent React reconciliation issues
      try {
        if (link.parentNode === document.body) {
          document.body.removeChild(link);
        }
      } catch (removeError) {
        console.warn('Failed to remove download link (non-critical):', removeError);
      }

      URL.revokeObjectURL(url);

      console.log('Research report exported successfully');
    } catch (error) {
      console.error('Error exporting research report:', error);
    }
  };

  // Download action button (will appear on the right)
  // Mirrors slideshow button style: dark on light mode, light on dark mode
  const downloadButton = !isLoading && (fullReport || report) ? (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        handleExportPDF();
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(0.5, 1.25),
        borderRadius: getChatRadii(theme).pill,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        // Prominent monochrome styling matching slideshow button
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#1a1a1a',
        color: theme.palette.mode === 'dark' ? '#0a0a0a' : '#ffffff',
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 1)' : '#000000',
          transform: 'scale(1.02)'
        }
      }}
    >
      <Typography sx={{
        fontSize: theme.typography.pxToRem(11),
        fontWeight: theme.typography.fontWeightMedium,
        lineHeight: 1.2
      }}>
        Download
      </Typography>
    </Box>
  ) : null;

  return (
    <UnifiedCard
      icon={<PublicIcon />}
      title={finalTitle}
      actionButton={downloadButton}
      isLoading={isLoading}
      isCollapsible={true}
      defaultExpanded={true}
      noPadding={true}
      subtitle={isErrorStatus ? undefined : finalSubtitle}
    >
      {/* Single scroll area for citations */}
      <Box sx={{
        maxHeight: theme.spacing(62.5),
        overflowY: 'auto',
        overflowX: 'hidden',
        // Minimal scrollbar styling
        '&::-webkit-scrollbar': {
          width: theme.spacing(tokens.spacing.nano + tokens.spacing.quark)
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
          border: 'none',
          margin: 0,
          padding: 0
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.3 : 0.18),
          borderRadius: getChatRadii(theme).pill,
          border: 'none',
          '&:hover': {
            backgroundColor: theme => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.45 : 0.28)
          }
        },
        scrollbarWidth: 'thin',
        scrollbarColor: `${alpha(theme.palette.text.primary, 0.2)} transparent`
      }}>
        {/* Show minimal loading state if no content yet and no sources */}
        {isLoading && parsedReport.citations.length === 0 && sourceEntries.length === 0 && (
          <Box sx={{ padding: theme.spacing(tokens.spacing.md), minHeight: theme.spacing(7.5) }} />
        )}

        {/* Error status */}
        {isErrorStatus && (
          <Box sx={{ padding: theme.spacing(tokens.spacing.md) }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.error.main,
                fontWeight: theme.typography.fontWeightMedium
              }}
            >
              {normalizedStatus}
            </Typography>
          </Box>
        )}

        {/* Sources Section - every search run and every page read, live */}
        {sourceEntries.length > 0 && (
          <Box
            aria-label="Sources consulted"
            sx={{
              paddingX: theme.spacing(tokens.spacing.md),
              paddingTop: theme.spacing(tokens.spacing.xs),
              paddingBottom: theme.spacing(tokens.spacing.sm)
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: theme.spacing(tokens.spacing.xs),
              marginBottom: theme.spacing(tokens.spacing.xs)
            }}>
              <Typography sx={{
                fontSize: theme.typography.pxToRem(12),
                fontWeight: theme.typography.fontWeightMedium,
                color: alpha(theme.palette.text.primary, 0.6),
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Sources
              </Typography>
              {sourcesSummary && (
                <Typography sx={{
                  fontSize: theme.typography.pxToRem(11),
                  color: alpha(theme.palette.text.secondary, 0.7),
                  whiteSpace: 'nowrap'
                }}>
                  {sourcesSummary}
                </Typography>
              )}
            </Box>

            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(tokens.spacing.xs)
            }}>
              {visibleSourceEntries.map((entry) => {
                // Per-row shimmer: a search row is active iff the card is still
                // loading AND that search has not been marked completed AND no
                // report content exists yet. The per-row completed flag is
                // flipped by streamProcessor when (a) a newer search arrives,
                // (b) this search's tool_complete fires, or (c) the research
                // sub-agent finishes (defensive sweep). The isLoading guard
                // makes this safe for resumed sessions whose persisted entries
                // predate the completed field. A fetched page is only ever
                // recorded once the fetch has been issued, so it never
                // shimmers.
                const hasReportContent = Boolean(parsedReport.content || fullReport || report);
                const isActiveQuery = entry.kind === 'search'
                  && isLoading
                  && !entry.completed
                  && !hasReportContent;

                // Tool arguments and tool results are model- and web-derived.
                // Only an http(s) URL ever becomes a real link; anything else
                // renders as plain text.
                const safeHref = entry.kind === 'page' && normalizeSourceUrl(entry.url)
                  ? entry.url
                  : null;

                const linkProps = safeHref
                  ? {
                    component: 'a',
                    href: safeHref,
                    target: '_blank',
                    rel: 'noopener noreferrer'
                  }
                  : {};

                return (
                  <Box
                    key={entry.id}
                    {...linkProps}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing(tokens.spacing.xs),
                      minWidth: 0,
                      paddingY: theme.spacing(0.5),
                      paddingX: theme.spacing(1),
                      border: getChatBorders(theme).resting,
                      borderRadius: getChatRadii(theme).chip,
                      backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.02 : 0.01),
                      textDecoration: 'none',
                      color: 'inherit',
                      // Fade in animation for new sources
                      animation: 'fadeIn 0.3s ease-in',
                      '@keyframes fadeIn': {
                        from: { opacity: 0, transform: 'translateY(-4px)' },
                        to: { opacity: 1, transform: 'translateY(0)' }
                      },
                      ...(safeHref && {
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        }
                      }),
                      // Shimmer effect for active query
                      ...(isActiveQuery && {
                        position: 'relative',
                        overflow: 'hidden',
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.15)}, transparent)`,
                          animation: 'shimmer 1.5s infinite',
                        },
                        '@keyframes shimmer': {
                          '0%': { left: '-100%' },
                          '100%': { left: '100%' }
                        }
                      })
                    }}
                  >
                    {entry.kind === 'search' ? (
                      <SearchIcon sx={{
                        fontSize: theme.typography.pxToRem(13),
                        color: isActiveQuery ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.4),
                        flexShrink: 0,
                        transition: 'color 0.2s',
                        // Pulse animation for active query
                        ...(isActiveQuery && {
                          animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                          '@keyframes pulse': {
                            '0%, 100%': {
                              opacity: 1
                            },
                            '50%': {
                              opacity: 0.5
                            }
                          }
                        })
                      }} />
                    ) : (
                      <LinkIcon sx={{
                        fontSize: theme.typography.pxToRem(13),
                        color: alpha(theme.palette.text.secondary, 0.4),
                        flexShrink: 0
                      }} />
                    )}

                    {/* The domain leads a page row so provenance survives the
                        truncation that a task pane forces on long URLs. */}
                    <Typography
                      sx={{
                        fontSize: theme.typography.pxToRem(11),
                        lineHeight: 1.4,
                        color: isActiveQuery ? theme.palette.text.primary : alpha(theme.palette.text.secondary, 0.7),
                        fontWeight: entry.kind === 'page'
                          ? theme.typography.fontWeightMedium
                          : theme.typography.fontWeightRegular,
                        transition: 'color 0.2s',
                        minWidth: 0,
                        ...(entry.kind === 'page'
                          ? {
                            flexShrink: 0,
                            maxWidth: '55%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }
                          : { flex: 1 })
                      }}
                    >
                      {entry.primary}
                    </Typography>

                    {entry.kind === 'page' && entry.detail && (
                      <Typography
                        sx={{
                          fontSize: theme.typography.pxToRem(11),
                          lineHeight: 1.4,
                          color: alpha(theme.palette.text.secondary, 0.55),
                          fontWeight: theme.typography.fontWeightRegular,
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {entry.detail}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            {(hiddenSourceCount > 0 || showAllSources) && sourceEntries.length > VISIBLE_SOURCE_LIMIT && (
              <Button
                size="small"
                onClick={() => setShowAllSources(prev => !prev)}
                sx={{
                  marginTop: theme.spacing(tokens.spacing.nano),
                  paddingX: theme.spacing(tokens.spacing.nano),
                  minWidth: 0,
                  fontSize: theme.typography.pxToRem(11),
                  fontWeight: theme.typography.fontWeightMedium,
                  textTransform: 'none',
                  color: theme.palette.text.secondary
                }}
              >
                {showAllSources
                  ? 'Show fewer sources'
                  : `Show all ${sourceEntries.length} sources`}
              </Button>
            )}
          </Box>
        )}

        {/* Key Findings Section - Memoized */}
        {keyFindingsSection}

        {/* Research Content Section - Memoized */}
        {researchContentSection}

        {/* Citations Section - Memoized */}
        {citationsSection}
      </Box>
    </UnifiedCard>
  );
};

export const ResearchCard = React.memo(ResearchCardComponent);

// Extract domain from URL (same logic as ResearchStatusCard)
const extractDomain = (title, url) => {
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.replace('www.', '');

    // Skip if it's a redirect service
    if (domain.includes('vertexaisearch') || domain.includes('google')) {
      // Try to extract from title
      if (title) {
        const domainPattern = /(?:^|\s|-)([a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,})(?:$|\s|:|-)/;
        const matches = title.match(domainPattern);
        if (matches) {
          return matches[1].toLowerCase();
        }

        // Known sites
        const knownSites = {
          'BBC': 'bbc.com',
          'CNN': 'cnn.com',
          'The Guardian': 'theguardian.com',
          'Wikipedia': 'wikipedia.org',
          'YouTube': 'youtube.com',
          'Reddit': 'reddit.com',
          'Reuters': 'reuters.com',
          'Bloomberg': 'bloomberg.com',
          'Forbes': 'forbes.com',
          'TechCrunch': 'techcrunch.com'
        };

        for (const [siteName, domain] of Object.entries(knownSites)) {
          if (title.includes(siteName)) {
            return domain;
          }
        }
      }
      return 'web';
    }

    return domain;
  } catch (e) {
    return 'web';
  }
};


/**
 * TodoChecklist renders the agent's live task list with integrated timer.
 * Features a clean tree-style design similar to code block aesthetics.
 * Updates stream in place to stay in sync with orchestration progress.
 */
const TodoChecklistComponent = ({
  items = [],
  status = 'update',
  updatedAt = null,
  meta = null,
  timerData = null,
  creditsConsumed = null
}) => {
  const theme = useTheme();
  const tokens = getRestackTokens(theme);

  const normalizeMs = (value) => {
    if (!value) return null;
    if (typeof value === 'number') return value;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const resolvedTimerBase = useMemo(() => {
    if (!timerData) {
      return null;
    }

    const startMs = normalizeMs(timerData.startTime);
    const completedMs = normalizeMs(timerData.completedAt);

    // FIX: Timer should stop when:
    // 1. isStreaming is explicitly false, OR
    // 2. isActive is explicitly false, OR
    // 3. completedAt timestamp exists, OR
    // 4. isComplete flag is true
    const hasCompletionSignal = completedMs !== null
      || timerData?.isComplete === true
      || timerData?.isStreaming === false
      || timerData?.isActive === false
      || Boolean(timerData?.completedAt);  // Explicit check in case normalizeMs fails to parse

    const isStreaming = !hasCompletionSignal;

    let baseDuration = 0;
    if (typeof timerData.durationSeconds === 'number') {
      baseDuration = Math.max(0, timerData.durationSeconds);
    } else if (typeof timerData.elapsedSeconds === 'number') {
      baseDuration = Math.max(0, timerData.elapsedSeconds);
    } else if (startMs) {
      // IMPORTANT: Only use Date.now() for LIVE streaming, never for completed states
      // If completed but no completedMs, default to 0 (we don't have the actual duration)
      if (isStreaming) {
        baseDuration = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
      } else if (completedMs) {
        baseDuration = Math.max(0, Math.floor((completedMs - startMs) / 1000));
      }
      // If not streaming and no completedMs, baseDuration stays 0
    }

    return {
      baseDuration,
      isStreaming,
      startMs,
      completedMs,
      statusHint: timerData?.statusHint || null,
      statusLabel: timerData?.statusLabel || null
    };
  }, [timerData]);

  // Live timer that updates every second while streaming
  const [liveElapsed, setLiveElapsed] = useState(0);

  useEffect(() => {
    if (!resolvedTimerBase || !resolvedTimerBase.isStreaming) {
      setLiveElapsed(0);
      return;
    }

    // Update immediately
    if (resolvedTimerBase.startMs) {
      setLiveElapsed(Math.max(0, Math.floor((Date.now() - resolvedTimerBase.startMs) / 1000)));
    }

    // Then update every second
    const interval = setInterval(() => {
      if (resolvedTimerBase.startMs) {
        setLiveElapsed(Math.max(0, Math.floor((Date.now() - resolvedTimerBase.startMs) / 1000)));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [resolvedTimerBase]);

  const resolvedTimer = useMemo(() => {
    if (!resolvedTimerBase) {
      return null;
    }

    // An absurdity guard for a nonsense elapsed time, NOT a limit on how long
    // work may take. It was 10 minutes and it did two harmful things: it froze
    // the number for every turn that ran longer, which a workbook build does
    // routinely, and it forced the card into its completed state while the turn
    // was still streaming. A clock that stops and calls itself finished while
    // the model is working is worse than no clock.
    //
    // Six hours is past any real turn and still catches the stale record the
    // guard was written for. Reaching it no longer changes state: see below.
    const MAX_DISPLAY_DURATION_SECONDS = 6 * 60 * 60;

    const rawElapsed = resolvedTimerBase.isStreaming && liveElapsed > 0
      ? liveElapsed
      : resolvedTimerBase.baseDuration;

    // Cap the displayed duration
    const elapsedSeconds = Math.min(rawElapsed, MAX_DISPLAY_DURATION_SECONDS);

    // The cap clamps the NUMBER and nothing else. It used to also flip the card
    // to complete, which meant a long turn reported itself finished while it was
    // still running, and the user had no way to tell that from a hang.
    // Whether a turn is streaming is the turn's business, never the clock's.
    if (rawElapsed >= MAX_DISPLAY_DURATION_SECONDS && resolvedTimerBase.isStreaming) {
      console.warn('⏱️ Timer display clamped, turn still streaming:', { rawElapsed, max: MAX_DISPLAY_DURATION_SECONDS });
    }

    return {
      elapsedSeconds,
      isStreaming: resolvedTimerBase.isStreaming,
      statusHint: resolvedTimerBase.statusHint,
      statusLabel: resolvedTimerBase.statusLabel
    };
  }, [resolvedTimerBase, liveElapsed]);

  const hasItems = Array.isArray(items) && items.length > 0;
  const hasTimer = resolvedTimer && resolvedTimer.elapsedSeconds > 0;

  // Don't render if no items and no timer
  if (!hasItems && !hasTimer) {
    return null;
  }

  const isStreaming = resolvedTimer?.isStreaming || false;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0)
      }}
    >
      {/* Header line matching AgentStepDot style */}
      {hasTimer && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(tokens.spacing.sm),
            flexWrap: 'wrap',
            marginBottom: hasItems ? theme.spacing(1.5) : 0
          }}
        >
          {/* Pulsing dot matching AgentStepDot */}
          <Box
            sx={{
              width: theme.spacing(0.8),
              height: theme.spacing(0.8),
              borderRadius: getChatRadii(theme).circle,
              background: theme.palette.info.main,
              flexShrink: 0,
              animation: isStreaming ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': {
                  opacity: 1,
                  transform: 'scale(1)'
                },
                '50%': {
                  opacity: 0.5,
                  transform: 'scale(0.85)'
                }
              }
            }}
          />

          {/* Status text */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: theme.typography.fontWeightMedium,
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
              fontSize: theme.typography.pxToRem(13),
              color: theme.palette.text.primary
            }}
          >
            {isStreaming ? 'Running' : 'Completed'}
          </Typography>

          {/* Timer inline */}
          <Typography
            variant="body2"
            sx={{
              fontSize: theme.typography.pxToRem(13),
              color: alpha(theme.palette.text.secondary, 0.85),
              lineHeight: 1.4
            }}
          >
            • {formatDurationLabel(resolvedTimer.elapsedSeconds)}
            {/* Per-message credit/cost figures are intentionally NOT shown to
                members (adoption guard) — the org pool lives in the Admin panel. */}
            {resolvedTimer.statusHint && ` • ${resolvedTimer.statusHint}`}
          </Typography>
        </Box>
      )}

      {/* Tree-style todo list - unified chronological tree */}
      {hasItems && (() => {
        // Keep items in chronological order (as received from backend)
        // No need to filter into sections - render all items in one unified tree

        const renderItem = (item, isLast) => {
          const itemStatus = normalizeTodoStatus(item.status);
          const isComplete = itemStatus === 'completed';
          const isRunning = itemStatus === 'in_progress';
          const isFailed = itemStatus === 'failed';
          const isSkipped = itemStatus === 'skipped';

          return (
            <Box
              key={item.id}
              component="li"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(1.25),
                position: 'relative',
                paddingLeft: theme.spacing(2.5),
                minHeight: theme.spacing(3),
                ...(isRunning && {
                  backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
                  borderRadius: getChatRadii(theme).nested,
                  // Running rail: semantic accent hue at the shared emphasis width.
                  borderLeft: `${getChatBorders(theme).emphasisWidth}px solid ${theme.palette.primary.main}`,
                  marginLeft: theme.spacing(-1),
                  marginRight: theme.spacing(-1),
                  paddingLeft: theme.spacing(3.2),
                  paddingRight: theme.spacing(1),
                  paddingTop: theme.spacing(0.75),
                  paddingBottom: theme.spacing(0.75)
                })
              }}
            >
              {/* Tree branch connector */}
              <Box
                sx={{
                  position: 'absolute',
                  left: theme.spacing(0.5),
                  top: 0,
                  bottom: 0,
                  width: theme.spacing(1.5),
                  pointerEvents: 'none',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    width: theme.spacing(1.5),
                    height: '1px',
                    backgroundColor: alpha(
                      theme.palette.divider,
                      theme.palette.mode === 'dark' ? 0.4 : 0.25
                    )
                  },
                  '&::after': !isLast ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    backgroundColor: alpha(
                      theme.palette.divider,
                      theme.palette.mode === 'dark' ? 0.4 : 0.25
                    )
                  } : {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '50%',
                    width: '1px',
                    backgroundColor: alpha(
                      theme.palette.divider,
                      theme.palette.mode === 'dark' ? 0.4 : 0.25
                    )
                  }
                }}
              />

              {/* Status indicator */}
              <Box
                sx={{
                  width: theme.spacing(1),
                  height: theme.spacing(1),
                  flexShrink: 0,
                  borderRadius: getChatRadii(theme).circle,
                  border: `${getChatBorders(theme).emphasisWidth}px solid ${isRunning
                    ? theme.palette.primary.main
                    : isComplete
                      ? theme.palette.success.main
                      : isFailed
                        ? theme.palette.error.main
                        : isSkipped
                          ? alpha(theme.palette.text.disabled, 0.4)
                          : alpha(theme.palette.text.secondary, 0.3)
                    }`,
                  backgroundColor: isRunning
                    ? theme.palette.primary.main
                    : isComplete
                      ? theme.palette.success.main
                      : isFailed
                        ? theme.palette.error.main
                        : 'transparent',
                  transition: 'all 0.2s ease',
                  animation: isRunning ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      opacity: 1,
                      transform: 'scale(1)'
                    },
                    '50%': {
                      opacity: 0.5,
                      transform: 'scale(0.85)'
                    }
                  }
                }}
              />

              {/* Task content */}
              <Typography
                variant="body2"
                sx={{
                  color: isRunning
                    ? theme.palette.primary.main
                    : isFailed
                      ? theme.palette.error.main
                      : theme.palette.text.primary,
                  textDecoration: (isComplete || isSkipped) ? 'line-through' : 'none',
                  textDecorationThickness: (isComplete || isSkipped) ? '1.5px' : undefined,
                  textDecorationColor: (isComplete || isSkipped) ? alpha(theme.palette.text.primary, 0.6) : undefined,
                  fontSize: theme.typography.body2?.fontSize || '0.875rem',
                  lineHeight: 1.5,
                  fontFamily: theme.typography.fontFamily,
                  transition: 'color 0.2s ease, text-decoration 0.2s ease',
                  fontWeight: isRunning ? 600 : 400,
                  flex: 1,
                  opacity: (isComplete || isSkipped) ? 0.6 : 1
                }}
              >
                {item.content}
              </Typography>
            </Box>
          );
        };

        // Render all items in one unified chronological list
        return (
          <Box
            component="ul"
            sx={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(1),
              paddingLeft: theme.spacing(1.8)
            }}
          >
            {items.map((item, idx) => renderItem(item, idx === items.length - 1))}
          </Box>
        );
      })()}
    </Box>
  );
};

export const TodoChecklist = React.memo(TodoChecklistComponent);

// ClarificationCard removed: backend never emitted clarification events,
// and the plan-decision approval UI is gone.
